<template>
    <div class="row">
        <div class="col-12 form-group">
            <label for="roles">Roles</label>
            <v-select
                :value="user.roles"
                :options="availableRoles"
                :clearable="false"
                label="text"
                :reduce="option => option.value"
                :class="{
                    'is-invalid': hasError('roles')
                }"
                multiple
                :disabled="!hasUserRolesPermission && editMode"
                @input="onRolesChange"
            />
            <div class="invalid-feedback">
                {{ getError('roles') }}
            </div>
        </div>
        <div class="col-12 form-group">
            <label for="email">Email</label>
            <input
                id="email"
                v-model="user.email"
                class="form-control"
                :class="{
                    'is-invalid': hasError('email')
                }"
                type="text"
            />
            <div class="invalid-feedback">
                {{ getError('email') }}
            </div>
        </div>
        <div class="col-12 form-group">
            <label for="tz">TZ</label>
            <input
                id="tz"
                v-model="user.tz"
                class="form-control"
                :class="{
                    'is-invalid': hasError('tz')
                }"
                type="text"
            />
            <div class="invalid-feedback">
                {{ getError('tz') }}
            </div>
        </div>
        <div class="col-6 form-group">
            <label for="firstName">First Name</label>
            <input
                id="firstName"
                v-model="user.firstName"
                class="form-control"
                :class="{ 'is-invalid': hasError('firstName') }"
                type="text"
                dir="auto"
            />
            <div class="invalid-feedback">
                {{ getError('firstName') }}
            </div>
        </div>
        <div class="col-6 form-group">
            <label for="lastName">Last Name</label>
            <input
                id="lastName"
                v-model="user.lastName"
                class="form-control"
                :class="{ 'is-invalid': hasError('lastName') }"
                type="text"
                dir="auto"
            />
            <div class="invalid-feedback">
                {{ getError('lastName') }}
            </div>
        </div>
        <div class="col-6 form-group">
            <label for="phone">Phone number</label>
            <input
                id="phone"
                v-model="user.phone"
                class="form-control"
                :class="{ 'is-invalid': hasError('phone') }"
                type="text"
                @input="$emit('phone-changed')"
            />
            <div class="invalid-feedback">
                {{ getError('phone') }}
            </div>
        </div>

        <div class="col-6 form-group">
            <label for="hugs">Hugs</label>
            <v-select
                v-model="user.hugs"
                label="number"
                :options="hugs"
                multiple
                :clearable="false"
                :class="{
                    'is-invalid': hasError('hugs')
                }"
                @search="onHugsSearch"
            />
            <div class="invalid-feedback">
                {{ getError('hugs') }}
            </div>
        </div>

        <permissions-selector
            v-if="user.roles.includes(ADMIN_ROLE.value)"
            v-model="user.permissions"
            :disabled="!hasUserRolesPermission"
            class="col-12 form-group"
        />
    </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import PermissionsSelector from './PermissionsSelector';

export default {
    components: {
        PermissionsSelector
    },

    props: {
        value: {
            type: Object,
            required: false,
            default: () => ({})
        },
        editMode: {
            type: Boolean,
            required: false,
            default: false
        },
        validation: {
            type: Object,
            required: true
        },
        serverErrors: {
            type: Array,
            required: true
        }
    },

    data() {
        return {
            user: {
                roles: []
            },
            hugs: [],
            timer: 0
        };
    },

    computed: {
        ...mapGetters({
            USER_ROLE: 'roles/USER',
            AGUDA_ROLE: 'roles/AGUDA',
            ADMIN_ROLE: 'roles/ADMIN',
            ALL_ROLES: 'roles/ALL_ROLES',
            loggedUser: 'auth/loggedUser',
            HEAD_ADMIN_ROLE: 'roles/HEAD_ADMIN',
            USER_ROLES_PERMISSION: 'acls/USER_ROLES_PERMISSION'
        }),

        hasUserRolesPermission() {
            return (
                (this.loggedUser?.acl?.permissions &&
                    !!this.loggedUser.acl.permissions[
                        this.USER_ROLES_PERMISSION
                    ]) ||
                this.loggedUser?.acl?.permissions?.all
            );
        },

        availableRoles() {
            if (this.hasUserRolesPermission || this.editMode) {
                return this.ALL_ROLES;
            }

            return [this.USER_ROLE];
        }
    },

    watch: {
        user() {
            this.$emit('input', this.user);
        }
    },

    mounted() {
        this.user = { ...this.value };
    },

    methods: {
        ...mapActions({
            getHugs: 'hugs/index'
        }),

        onRolesChange(value) {
            let roles = value;
            const { value: userRole } = this.USER_ROLE;
            const { value: agudaRole } = this.AGUDA_ROLE;

            if (roles.includes(userRole) && roles.includes(agudaRole)) {
                if (this.user.roles.includes(userRole)) {
                    roles = roles.filter(role => role !== userRole);
                } else {
                    roles = roles.filter(role => role !== agudaRole);
                }
            }

            const { value: adminRole } = this.ADMIN_ROLE;
            const { value: headAdminRole } = this.HEAD_ADMIN_ROLE;

            if (roles.includes(adminRole) && roles.includes(headAdminRole)) {
                if (this.user.roles.includes(adminRole)) {
                    roles = roles.filter(role => role !== adminRole);
                } else {
                    roles = roles.filter(role => role !== headAdminRole);
                }
            }

            this.user.roles = roles;
        },

        async fetchHugs(search) {
            try {
                const { rows: hugs } = await this.getHugs({
                    search
                });

                this.hugs = hugs;
            } catch (err) {
                console.error(err);

                this.$toasted.global.error();
            }
        },

        async onHugsSearch(search, loading) {
            if (search.length) {
                loading(true);

                if (this.timer) {
                    clearTimeout(this.timer);
                    this.timer = null;
                }

                this.timer = setTimeout(async () => {
                    await this.fetchHugs(search);

                    loading(false);
                }, 500);
            }
        },

        hasError(key) {
            return (
                this.validation.user[key].$error ||
                this.serverErrors.find(error => error.param === key)
            );
        },

        getError(key) {
            return this.$getValidationErrorMessageHandler.handle(
                this.validation.user,
                this.serverErrors,
                key
            );
        }
    }
};
</script>
